import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Activity, Cpu, MemoryStick, Users, Server, Zap, Gauge,
  Database, ShieldAlert, Timer, RefreshCw, Pause, Play, AlertTriangle, CheckCircle2, X, Trash2, Loader2,
  Globe, UserCheck, TrendingUp, CalendarDays,
} from "lucide-react";

// Country-code → emoji flag (regional indicator letters).
function flagEmoji(code) {
  if (!code || code.length !== 2 || code === "??") return "🌐";
  try {
    return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
  } catch { return "🌐"; }
}

function AudienceSection({ audience, days, onDays }) {
  const daily = audience?.daily || [];
  const countries = audience?.countries || [];
  const maxV = Math.max(1, ...daily.map((d) => d.visitors));
  const totalCountryVisitors = Math.max(1, countries.reduce((s, c) => s + c.visitors, 0));
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4 sm:p-5" data-testid="audience-section">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h3 className="font-bold text-slate-800 flex items-center gap-2">
          <Users className="w-4 h-4 text-sky-600" /> Audiência
        </h3>
        <div className="flex items-center gap-1" data-testid="audience-range">
          {[7, 14, 30].map((d) => (
            <button key={d} type="button" onClick={() => onDays(d)}
              className={`px-2 py-1 text-xs rounded-md border ${days === d ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
              data-testid={`audience-range-${d}`}>{d}d</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Card icon={CalendarDays} tone="sky" label="Visitantes hoje" value={audience?.today_visitors ?? "—"} testId="aud-today" />
        <Card icon={UserCheck} tone="emerald" label="Em simultâneo agora" value={audience?.concurrent_now ?? "—"} hint="ativos nos últimos 5 min" testId="aud-concurrent" />
        <Card icon={TrendingUp} tone="violet" label="Pico simultâneo hoje" value={audience?.peak_today ?? "—"} testId="aud-peak" />
        <Card icon={Users} tone="amber" label={`Total (${days} dias)`} value={audience?.total_window ?? "—"} testId="aud-total" />
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Visitantes por dia</div>
          <div className="flex items-end gap-1 h-36 border-b border-slate-200 pb-0" data-testid="audience-daily-bars">
            {daily.map((d) => (
              <div key={d.date} className="flex-1 min-w-0 h-full flex flex-col items-center justify-end group" title={`${d.date}: ${d.visitors}`}>
                <div className="text-[9px] text-slate-500 tabular-nums mb-0.5">{d.visitors || ""}</div>
                <div className="w-full rounded-t bg-gradient-to-t from-sky-500 to-sky-300 transition-all"
                  style={{ height: `${Math.max(2, (d.visitors / maxV) * 100)}%` }} />
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[9px] text-slate-400 mt-1">
            <span>{daily[0]?.date?.slice(5)}</span>
            <span>{daily[daily.length - 1]?.date?.slice(5)}</span>
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1">
            <Globe className="w-3.5 h-3.5" /> Países de acesso
          </div>
          {countries.length === 0 ? (
            <div className="text-xs text-slate-400 py-4">Sem dados ainda.</div>
          ) : (
            <div className="space-y-2 max-h-40 overflow-y-auto pr-1" data-testid="audience-countries">
              {countries.map((c) => (
                <div key={c.country_code} className="flex items-center gap-2 text-sm">
                  <span className="text-base leading-none">{flagEmoji(c.country_code)}</span>
                  <span className="w-28 truncate text-slate-700">{c.country}</span>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-sky-500" style={{ width: `${(c.visitors / totalCountryVisitors) * 100}%` }} />
                  </div>
                  <span className="w-8 text-right tabular-nums text-slate-600">{c.visitors}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <p className="text-[10px] text-slate-400 mt-4">
        Visitantes = IPs únicos por dia. O país é obtido por geolocalização do IP (pode demorar alguns minutos a resolver; acessos internos aparecem como "Rede interna").
      </p>
    </div>
  );
}

const API = process.env.REACT_APP_BACKEND_URL;

// Auto-refresh cadence. 3 s is snappy enough for a live dashboard and gentle
// enough to not add measurable load to the backend even under peak.
const POLL_MS = 3000;
// How many samples to keep for the mini-trend charts (~2 min at 3s cadence).
const HISTORY_MAX = 40;

function fmtDuration(sec) {
  if (!sec && sec !== 0) return "—";
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
}
function fmtMs(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `${n.toFixed ? n.toFixed(1) : n} ms`;
}

// Compact sparkline built with plain SVG so we have zero chart deps.
function Sparkline({ values = [], stroke = "#0ea5e9", height = 32, width = 120 }) {
  if (!values.length) return <div style={{ height, width }} />;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(1, max - min);
  const step = values.length > 1 ? width / (values.length - 1) : width;
  const points = values.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / span) * height;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg width={width} height={height} className="block">
      <polyline fill="none" stroke={stroke} strokeWidth={1.5} points={points} />
    </svg>
  );
}

// Multi-series chart drawn as pure SVG. Each series is `{label, color, key}`;
// values are pulled from `points[].[key]`. X-axis is `ts` (epoch seconds).
function HistoryChart({ points = [], series = [], height = 220 }) {
  const width = 900;
  const paddingL = 40, paddingR = 12, paddingT = 12, paddingB = 30;
  const innerW = width - paddingL - paddingR;
  const innerH = height - paddingT - paddingB;
  if (!points.length) {
    return (
      <div style={{ height }} className="grid place-items-center text-sm text-slate-400">
        Sem dados no intervalo selecionado. Aguarda alguns minutos para acumular snapshots (1 por minuto).
      </div>
    );
  }
  const xs = points.map((p) => p.ts);
  const xMin = Math.min(...xs), xMax = Math.max(...xs);
  const xSpan = Math.max(1, xMax - xMin);
  const allYs = series.flatMap((s) => points.map((p) => Number(p[s.key] || 0)));
  const yMin = 0;
  const yMax = Math.max(1, ...allYs);
  const px = (t) => paddingL + ((t - xMin) / xSpan) * innerW;
  const py = (v) => paddingT + innerH - ((v - yMin) / (yMax - yMin)) * innerH;
  // 5 horizontal grid lines
  const gridY = [0, 0.25, 0.5, 0.75, 1].map((f) => yMin + f * (yMax - yMin));
  // ~6 x labels
  const stepX = points.length > 6 ? Math.floor(points.length / 6) : 1;
  const xLabels = points.filter((_, i) => i % stepX === 0);
  const fmtTime = (t) => {
    const d = new Date(t * 1000);
    const H = String(d.getHours()).padStart(2, "0");
    const M = String(d.getMinutes()).padStart(2, "0");
    return `${H}:${M}`;
  };
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} preserveAspectRatio="none">
        {/* grid */}
        {gridY.map((v, i) => (
          <g key={i}>
            <line x1={paddingL} x2={width - paddingR} y1={py(v)} y2={py(v)} stroke="#e2e8f0" strokeDasharray="3 3" />
            <text x={paddingL - 6} y={py(v) + 3} fontSize="10" fill="#94a3b8" textAnchor="end">{Math.round(v)}</text>
          </g>
        ))}
        {/* series */}
        {series.map((s) => {
          const path = points.map((p, i) => {
            const x = px(p.ts).toFixed(1);
            const y = py(Number(p[s.key] || 0)).toFixed(1);
            return `${i === 0 ? "M" : "L"} ${x} ${y}`;
          }).join(" ");
          return <path key={s.key} d={path} fill="none" stroke={s.color} strokeWidth={1.7} />;
        })}
        {/* x axis labels */}
        {xLabels.map((p, i) => (
          <text key={i} x={px(p.ts)} y={height - 8} fontSize="10" fill="#64748b" textAnchor="middle">{fmtTime(p.ts)}</text>
        ))}
      </svg>
      <div className="flex flex-wrap gap-3 mt-2 justify-center">
        {series.map((s) => (
          <span key={s.key} className="text-[11px] inline-flex items-center gap-1 text-slate-600">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, hint, tone = "sky", trend, testId, onClick }) {
  const tones = {
    sky:    { bg: "bg-sky-50",    text: "text-sky-700",    ring: "ring-sky-200",    line: "#0ea5e9" },
    emerald:{ bg: "bg-emerald-50",text: "text-emerald-700",ring: "ring-emerald-200",line: "#10b981" },
    amber:  { bg: "bg-amber-50",  text: "text-amber-700",  ring: "ring-amber-200",  line: "#f59e0b" },
    rose:   { bg: "bg-rose-50",   text: "text-rose-700",   ring: "ring-rose-200",   line: "#e11d48" },
    slate:  { bg: "bg-slate-50",  text: "text-slate-700",  ring: "ring-slate-200",  line: "#475569" },
    violet: { bg: "bg-violet-50", text: "text-violet-700", ring: "ring-violet-200", line: "#8b5cf6" },
  }[tone] || {};
  const clickable = typeof onClick === "function";
  const Wrapper = clickable ? "button" : "div";
  return (
    <Wrapper
      onClick={onClick}
      className={`text-left rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow ring-1 ${tones.ring} ${clickable ? "hover:ring-2 cursor-pointer" : ""}`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={`w-9 h-9 rounded-lg ${tones.bg} ${tones.text} grid place-items-center`}>
          {Icon ? <Icon className="w-5 h-5" /> : null}
        </div>
        {trend ? <Sparkline values={trend} stroke={tones.line} /> : null}
      </div>
      <div className="mt-3 text-2xl font-bold text-slate-900 tabular-nums">{value}</div>
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mt-1">{label}</div>
      {hint ? <div className="text-[11px] text-slate-400 mt-1">{hint}</div> : null}
      {clickable ? <div className="text-[10px] text-sky-600 font-bold mt-1">Ver detalhes →</div> : null}
    </Wrapper>
  );
}

function LatencyRow({ name, bucket, testId }) {
  return (
    <tr data-testid={testId}>
      <td className="py-2 pr-3 font-semibold text-slate-700">{name}</td>
      <td className="py-2 pr-3 text-slate-500 tabular-nums">{bucket?.count ?? 0}</td>
      <td className="py-2 pr-3 tabular-nums">{fmtMs(bucket?.avg_ms)}</td>
      <td className="py-2 pr-3 tabular-nums">{fmtMs(bucket?.p50_ms)}</td>
      <td className="py-2 tabular-nums">{fmtMs(bucket?.p95_ms)}</td>
    </tr>
  );
}

export default function MetricsPanel() {
  const { adminAuth } = useAuth();
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState({ active: [], recent: [], rules: [] });
  const [history, setHistory] = useState({ points: [], window: "1h", bucket_sec: 60 });
  const [historyWindow, setHistoryWindow] = useState("1h");
  const [editingRule, setEditingRule] = useState(null); // rule_id being edited
  const [ruleDraft, setRuleDraft] = useState({});
  const [savingRule, setSavingRule] = useState(false);
  // Sessions modal
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [sessionsData, setSessionsData] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsFilter, setSessionsFilter] = useState("all"); // all | competition | eliminatoria | vs_human
  const [cleanupHours, setCleanupHours] = useState(24);
  const [cleaning, setCleaning] = useState(false);
  const [err, setErr] = useState(null);
  const [paused, setPaused] = useState(false);
  const [lastFetchAt, setLastFetchAt] = useState(null);
  // Alert IDs already announced via toast (so we don't spam every poll).
  const announcedRef = useRef(new Set());
  const historyRef = useRef({
    rps: [],
    active: [],
    cpu: [],
    ram_pct: [],
    replicas: [],
    ping: [],
  });
  // Force re-render when history mutates
  const [, tick] = useState(0);
  // Audience analytics (daily visitors, concurrent, countries)
  const [audience, setAudience] = useState(null);
  const [audienceDays, setAudienceDays] = useState(14);
  // Incident log (production errors / slow requests)
  const [logs, setLogs] = useState(null);
  const [logsHours, setLogsHours] = useState(24);
  const [logsKind, setLogsKind] = useState("");
  const [clearingLogs, setClearingLogs] = useState(false);

  const headers = useMemo(
    () => (adminAuth?.token ? { Authorization: `Bearer ${adminAuth.token}` } : {}),
    [adminAuth?.token]
  );

  useEffect(() => {
    let cancelled = false;
    const fetchAudience = async () => {
      try {
        const { data: a } = await axios.get(`${API}/api/admin/metrics/audience?days=${audienceDays}`, { headers });
        if (!cancelled) setAudience(a);
      } catch { /* silent */ }
    };
    fetchAudience();
    const id = setInterval(fetchAudience, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [headers, audienceDays]);

  useEffect(() => {
    let cancelled = false;
    const fetchHistory = async () => {
      try {
        const { data: h } = await axios.get(`${API}/api/admin/metrics/history?window=${historyWindow}`, { headers });
        if (!cancelled) setHistory(h || { points: [], window: historyWindow });
      } catch { /* silent */ }
    };
    fetchHistory();
    // History updates less often — every 30s is enough.
    const id = setInterval(fetchHistory, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [headers, historyWindow]);

  useEffect(() => {
    let cancelled = false;
    const fetchLogs = async () => {
      try {
        const qs = `hours=${logsHours}${logsKind ? `&kind=${logsKind}` : ""}`;
        const { data: l } = await axios.get(`${API}/api/admin/metrics/logs?${qs}`, { headers });
        if (!cancelled) setLogs(l);
      } catch { /* silent */ }
    };
    fetchLogs();
    const id = setInterval(fetchLogs, 20000);
    return () => { cancelled = true; clearInterval(id); };
  }, [headers, logsHours, logsKind]);

  const clearLogs = async () => {
    if (!window.confirm("Limpar TODO o registo de incidentes?\n\nEsta ação não pode ser desfeita.")) return;
    setClearingLogs(true);
    try {
      await axios.post(`${API}/api/admin/metrics/logs/clear`, null, { headers });
      const { data: l } = await axios.get(`${API}/api/admin/metrics/logs?hours=${logsHours}`, { headers });
      setLogs(l);
      toast.success("Registo de incidentes limpo.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao limpar registo.");
    } finally { setClearingLogs(false); }
  };

  const startEditRule = (r) => {
    setEditingRule(r.id);
    setRuleDraft({
      threshold: r.threshold,
      duration_sec: r.duration_sec,
      email_after_sec: r.email_after_sec || 0,
      enabled: r.enabled !== false,
    });
  };
  const cancelEditRule = () => { setEditingRule(null); setRuleDraft({}); };
  const saveRule = async (ruleId) => {
    setSavingRule(true);
    try {
      await axios.put(`${API}/api/admin/metrics/rules/${ruleId}`, ruleDraft, { headers });
      const { data: al } = await axios.get(`${API}/api/admin/metrics/alerts`, { headers });
      setAlerts(al);
      toast.success("Regra atualizada.");
      cancelEditRule();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao guardar regra.");
    } finally { setSavingRule(false); }
  };
  const resetRule = async (ruleId) => {
    setSavingRule(true);
    try {
      await axios.post(`${API}/api/admin/metrics/rules/${ruleId}/reset`, null, { headers });
      const { data: al } = await axios.get(`${API}/api/admin/metrics/alerts`, { headers });
      setAlerts(al);
      toast.success("Regra reposta aos defaults.");
      cancelEditRule();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao repor.");
    } finally { setSavingRule(false); }
  };

  const openSessions = async () => {
    setSessionsOpen(true);
    setSessionsLoading(true);
    try {
      const { data: s } = await axios.get(`${API}/api/admin/metrics/sessions/live`, { headers });
      setSessionsData(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar sessões.");
    } finally { setSessionsLoading(false); }
  };
  // Auto-refresh while modal is open
  useEffect(() => {
    if (!sessionsOpen) return;
    const id = setInterval(async () => {
      try {
        const { data: s } = await axios.get(`${API}/api/admin/metrics/sessions/live`, { headers });
        setSessionsData(s);
      } catch { /* silent */ }
    }, 5000);
    return () => clearInterval(id);
  }, [sessionsOpen, headers]);

  const cleanupZombies = async () => {
    const h = Math.max(1, Math.floor(Number(cleanupHours) || 24));
    // Confirmation — this changes DB state, so make sure the user knows.
    if (!window.confirm(`Marcar como ABANDONADAS todas as sessões em curso com mais de ${h}h?\n\nOs contadores atualizam de imediato. A ação não pode ser desfeita.`)) return;
    setCleaning(true);
    try {
      const { data: r } = await axios.post(
        `${API}/api/admin/metrics/sessions/cleanup?older_than_hours=${h}`,
        null, { headers }
      );
      toast.success(`${r.total_abandoned} sessão(ões) marcada(s) como abandonadas.`);
      // Refresh list immediately
      const { data: s } = await axios.get(`${API}/api/admin/metrics/sessions/live`, { headers });
      setSessionsData(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro na limpeza.");
    } finally { setCleaning(false); }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const [{ data: d }, { data: al }] = await Promise.all([
          axios.get(`${API}/api/admin/metrics/live`, { headers }),
          axios.get(`${API}/api/admin/metrics/alerts`, { headers }),
        ]);
        if (cancelled) return;
        setData(d);
        setAlerts(al || { active: [], recent: [], rules: [] });
        setErr(null);
        setLastFetchAt(new Date());
        // Fire a toast for any newly active alert (not yet announced).
        const activeIds = new Set((al?.active || []).map((a) => `${a.rule_id}:${a.pod_id}:${Math.round(a.started_at)}`));
        (al?.active || []).forEach((a) => {
          const key = `${a.rule_id}:${a.pod_id}:${Math.round(a.started_at)}`;
          if (!announcedRef.current.has(key)) {
            announcedRef.current.add(key);
            const msg = `${a.label} — ${a.last_value}${a.unit} (pod ${String(a.pod_id).slice(0, 20)}…)`;
            if (a.severity === "critical") toast.error(msg, { duration: 8000 });
            else toast.warning(msg, { duration: 6000 });
          }
        });
        // Drop announcements that are no longer active so we can re-announce
        // if the same rule fires again later.
        [...announcedRef.current].forEach((k) => { if (!activeIds.has(k)) announcedRef.current.delete(k); });
        const h = historyRef.current;
        const push = (k, v) => { h[k].push(v); if (h[k].length > HISTORY_MAX) h[k].shift(); };
        push("rps", d?.requests?.rps_60s || 0);
        push("active", d?.active_users_5min || 0);
        const cpu = (d?.pods || []).reduce((s, p) => s + (p.process_cpu_pct || 0), 0);
        push("cpu", cpu);
        const ram = d?.pods?.[0]?.host_ram_pct || 0;
        push("ram_pct", ram);
        push("replicas", d?.replicas || 0);
        push("ping", d?.db_ping_ms || 0);
        tick((n) => n + 1);
      } catch (e) {
        if (cancelled) return;
        setErr(e?.response?.data?.detail || e?.message || "Erro");
      }
    };
    fetchOnce();
    if (paused) return () => { cancelled = true; };
    const id = setInterval(fetchOnce, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [headers, paused]);

  const h = historyRef.current;
  const pods = data?.pods || [];
  const req = data?.requests || {};
  const buckets = req.buckets || {};
  const sessions = data?.sessions || {};

  const totalSessions =
    (sessions.competition_in_progress || 0) +
    (sessions.eliminatoria_in_progress || 0) +
    (sessions.vs_human_open || 0);

  return (
    <div className="p-6 space-y-6" data-testid="metrics-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-sky-600" /> Métricas em tempo real
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Snapshot da plataforma — refresca automaticamente a cada {POLL_MS / 1000}s.
            Cada réplica do backend contribui com o seu heartbeat.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {lastFetchAt ? (
            <span data-testid="metrics-last-updated">
              Atualizado às <b>{lastFetchAt.toLocaleTimeString("pt-PT")}</b>
            </span>
          ) : null}
          <button
            onClick={() => setPaused((p) => !p)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-semibold"
            data-testid="metrics-toggle-pause"
          >
            {paused ? <><Play className="w-3.5 h-3.5" /> Retomar</> : <><Pause className="w-3.5 h-3.5" /> Pausar</>}
          </button>
        </div>
      </div>

      {err && (
        <div className="rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2" data-testid="metrics-error">
          <b>Erro ao obter métricas:</b> {String(err)}
        </div>
      )}

      {/* Active alerts banner */}
      {alerts.active.length > 0 && (
        <div className="rounded-xl border-2 border-rose-300 bg-gradient-to-r from-rose-50 to-amber-50 shadow-sm" data-testid="alerts-banner">
          <div className="px-4 py-3 flex items-center gap-2 border-b border-rose-200">
            <AlertTriangle className="w-5 h-5 text-rose-600 animate-pulse" />
            <div className="font-bold text-rose-800">
              {alerts.active.length} alerta{alerts.active.length > 1 ? "s" : ""} ativo{alerts.active.length > 1 ? "s" : ""}
            </div>
          </div>
          <ul className="divide-y divide-rose-100">
            {alerts.active.map((a) => {
              const started = new Date((a.started_at || 0) * 1000);
              const durationSec = Math.max(0, Math.round((data?.timestamp || 0) - (a.started_at || 0)));
              const isCritical = a.severity === "critical";
              return (
                <li
                  key={`${a.rule_id}:${a.pod_id}:${a.started_at}`}
                  className="px-4 py-3 flex items-start gap-3"
                  data-testid={`active-alert-${a.rule_id}`}
                >
                  <span className={`mt-1 inline-block w-2 h-2 rounded-full ${isCritical ? "bg-rose-600" : "bg-amber-500"} animate-pulse`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-800">
                      {a.label}
                      <span className={`ml-2 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${isCritical ? "bg-rose-600 text-white" : "bg-amber-500 text-white"}`}>
                        {a.severity}
                      </span>
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      Valor atual: <b className="tabular-nums">{a.last_value}{a.unit}</b>
                      {" · "}pico: <b className="tabular-nums">{a.peak_value}{a.unit}</b>
                      {" · "}threshold: {a.threshold}{a.unit} por ≥{a.duration_sec}s
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      Pod <code>{String(a.pod_id).slice(0, 32)}…</code> · começou {started.toLocaleTimeString("pt-PT")} · duração {fmtDuration(durationSec)}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Top row — big numbers */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <Card
          icon={Server}
          label="Réplicas ativas"
          value={data?.replicas ?? "—"}
          hint={`heartbeat ≤ 30s`}
          tone="violet"
          trend={h.replicas}
          testId="metric-replicas"
        />
        <Card
          icon={Users}
          label="Utilizadores ativos"
          value={data?.active_users_5min ?? "—"}
          hint="últimos 5 min"
          tone="emerald"
          trend={h.active}
          testId="metric-active-users"
        />
        <Card
          icon={Zap}
          label="Sessões em curso"
          value={totalSessions}
          hint={`comp ${sessions.competition_in_progress || 0} · elim ${sessions.eliminatoria_in_progress || 0} · vs ${sessions.vs_human_open || 0}`}
          tone="sky"
          testId="metric-sessions"
          onClick={openSessions}
        />
        <Card
          icon={Gauge}
          label="Pedidos / seg"
          value={(req.rps_60s ?? 0).toFixed(2)}
          hint={`${req.count_60s || 0} nos últimos 60s`}
          tone="sky"
          trend={h.rps}
          testId="metric-rps"
        />
        <Card
          icon={ShieldAlert}
          label="Bloqueios (429)"
          value={req.count_429_60s ?? 0}
          hint="rate-limits em 60s"
          tone={req.count_429_60s ? "rose" : "slate"}
          testId="metric-429"
        />
        <Card
          icon={Database}
          label="Ping MongoDB"
          value={fmtMs(data?.db_ping_ms)}
          hint="round-trip"
          tone={data?.db_ping_ms > 20 ? "amber" : "emerald"}
          trend={h.ping}
          testId="metric-db-ping"
        />
      </div>

      {/* Pods table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-bold text-slate-800 flex items-center gap-2">
            <Server className="w-4 h-4 text-violet-600" /> Réplicas ({pods.length})
          </h2>
          <div className="text-[11px] text-slate-400">Cada pod publica um heartbeat de 10 em 10 s.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pods-table">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Pod</th>
                <th className="text-left px-4 py-2">CPU proc.</th>
                <th className="text-left px-4 py-2">RSS proc.</th>
                <th className="text-left px-4 py-2">RAM host</th>
                <th className="text-left px-4 py-2">Load 1m/5m/15m</th>
                <th className="text-left px-4 py-2">Uptime</th>
                <th className="text-left px-4 py-2">Último HB</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pods.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">Ainda sem heartbeats. Aguarda ~10s…</td></tr>
              )}
              {pods.map((p) => {
                const isMe = p.pod_id === data?.this_pod_id;
                const ageS = data?.timestamp ? Math.max(0, Math.round(data.timestamp - p.last_seen)) : 0;
                return (
                  <tr key={p.pod_id} data-testid={`pod-row-${p.pod_id}`}>
                    <td className="px-4 py-2 font-mono text-xs">
                      {isMe && <span className="mr-1 inline-block w-2 h-2 rounded-full bg-emerald-500" title="Este pod" />}
                      <span className="text-slate-700">{p.pod_id.slice(0, 40)}</span>
                      {isMe && <span className="ml-1 text-[10px] font-bold text-emerald-700">(este)</span>}
                    </td>
                    <td className="px-4 py-2 tabular-nums">{p.process_cpu_pct?.toFixed(1) ?? "—"}%</td>
                    <td className="px-4 py-2 tabular-nums">{p.process_rss_mb?.toFixed(0) ?? "—"} MB</td>
                    <td className="px-4 py-2 tabular-nums">
                      {p.host_ram_used_mb?.toFixed(0) ?? "—"} / {p.host_ram_total_mb?.toFixed(0) ?? "—"} MB
                      <span className="text-[10px] ml-1 text-slate-400">({p.host_ram_pct?.toFixed(0) ?? "—"}%)</span>
                    </td>
                    <td className="px-4 py-2 tabular-nums text-[12px]">
                      {p.loadavg_1?.toFixed(2)} / {p.loadavg_5?.toFixed(2)} / {p.loadavg_15?.toFixed(2)}
                    </td>
                    <td className="px-4 py-2 tabular-nums">{fmtDuration(p.uptime_sec)}</td>
                    <td className="px-4 py-2 text-slate-500 tabular-nums">{ageS}s atrás</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Latency table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-4 py-3 border-b border-slate-100">
          <h2 className="font-bold text-slate-800 flex items-center gap-2">
            <Timer className="w-4 h-4 text-sky-600" /> Latência por grupo (últimos 60 s)
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="latency-table">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Grupo</th>
                <th className="text-left px-4 py-2">#</th>
                <th className="text-left px-4 py-2">Média</th>
                <th className="text-left px-4 py-2">p50</th>
                <th className="text-left px-4 py-2">p95</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 px-4">
              <LatencyRow name="Autenticação (auth)" bucket={buckets.auth} testId="latency-auth" />
              <LatencyRow name="Jogo (gameplay)" bucket={buckets.gameplay} testId="latency-gameplay" />
              <LatencyRow name="Outros /api" bucket={buckets.api} testId="latency-api" />
              <LatencyRow name="Fora de /api" bucket={buckets.other} testId="latency-other" />
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 text-[11px] text-slate-400">
          Grupos definidos em <code>backend/rate_limit.py</code> (auth 20/min, gameplay 240/min, api 120/min).
        </div>
      </div>

      {/* Audience analytics */}
      <AudienceSection audience={audience} days={audienceDays} onDays={setAudienceDays} />

      {/* Historical chart */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="history-panel">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-bold text-slate-800 flex items-center gap-2">
            <Activity className="w-4 h-4 text-sky-600" /> Evolução histórica
          </h2>
          <div className="flex items-center gap-1">
            {["1h", "6h", "24h", "7d"].map((w) => (
              <button
                key={w}
                onClick={() => setHistoryWindow(w)}
                className={`px-2.5 py-1 text-xs font-semibold rounded ${historyWindow === w ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid={`history-window-${w}`}
              >
                {w}
              </button>
            ))}
            <span className="ml-2 text-[10px] text-slate-400">{history.count || 0} pontos · bucket {history.bucket_sec}s</span>
          </div>
        </div>
        <div className="p-4 space-y-6">
          <div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Utilização (%)</div>
            <HistoryChart
              points={history.points || []}
              series={[
                { key: "cpu_pct", label: "CPU do pod", color: "#0ea5e9" },
                { key: "ram_pct", label: "RAM do host", color: "#8b5cf6" },
              ]}
            />
          </div>
          <div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Tráfego + latência</div>
            <HistoryChart
              points={history.points || []}
              series={[
                { key: "gameplay_p95_ms", label: "p95 gameplay (ms)", color: "#e11d48" },
                { key: "api_p95_ms", label: "p95 api (ms)", color: "#f59e0b" },
                { key: "rps", label: "req / s (×10)", color: "#10b981" },
              ]}
            />
          </div>
          <div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Utilizadores e sessões</div>
            <HistoryChart
              points={history.points || []}
              series={[
                { key: "active_users", label: "Utilizadores ativos", color: "#0ea5e9" },
                { key: "sessions_total", label: "Sessões em curso", color: "#8b5cf6" },
                { key: "replicas", label: "Réplicas", color: "#475569" },
              ]}
            />
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 text-[11px] text-slate-400">
          Snapshots gravados a cada 60s em <code>_metrics_snapshots</code> com TTL de 7 dias.
        </div>
      </div>

      {/* Incident log — durable registry of production errors & slow requests */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="incident-log-panel">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-bold text-slate-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-600" /> Registo de incidentes
            <span className="text-xs font-normal text-slate-400">erros, exceções, bloqueios e lentidão</span>
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1" data-testid="logs-range">
              {[1, 6, 24, 168].map((hval) => {
                const lbl = hval === 168 ? "7d" : `${hval}h`;
                return (
                  <button key={hval} type="button" onClick={() => setLogsHours(hval)}
                    className={`px-2.5 py-1 text-xs font-semibold rounded ${logsHours === hval ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                    data-testid={`logs-range-${lbl}`}>{lbl}</button>
                );
              })}
            </div>
            <button
              onClick={clearLogs}
              disabled={clearingLogs}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-slate-200 bg-white hover:bg-rose-50 text-rose-700 text-xs font-semibold disabled:opacity-60"
              data-testid="logs-clear-btn"
            >
              {clearingLogs ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Limpar
            </button>
          </div>
        </div>

        {/* Summary + kind filters */}
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          {[
            { k: "", label: "Todos" },
            { k: "5xx", label: "Erros 5xx" },
            { k: "exception", label: "Exceções" },
            { k: "429", label: "Bloqueios 429" },
            { k: "slow", label: "Lentos (>3s)" },
          ].map((f) => {
            const n = f.k === "" ? (logs?.total_events ?? 0) : (logs?.by_kind?.[f.k] ?? 0);
            const active = logsKind === f.k;
            return (
              <button key={f.k || "all"} type="button" onClick={() => setLogsKind(f.k)}
                className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${active ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                data-testid={`logs-kind-${f.k || "all"}`}>
                {f.label} <span className="opacity-70 tabular-nums">({n})</span>
              </button>
            );
          })}
          <span className="ml-auto text-[11px] text-slate-400">
            {logs?.total_events ?? 0} evento(s) em {logsHours === 168 ? "7 dias" : `${logsHours}h`} · retenção {logs?.ttl_days ?? 14}d
          </span>
        </div>

        <div className="overflow-x-auto max-h-[28rem] overflow-y-auto">
          <table className="w-full text-sm" data-testid="incident-log-table">
            <thead className="bg-slate-50 sticky top-0 text-slate-600 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Quando</th>
                <th className="text-left px-4 py-2">Tipo</th>
                <th className="text-left px-4 py-2">Estado</th>
                <th className="text-left px-4 py-2">Rota</th>
                <th className="text-right px-4 py-2">Nº</th>
                <th className="text-right px-4 py-2">Pior latência</th>
                <th className="text-left px-4 py-2">Detalhe</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(logs?.rows || []).length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                  <CheckCircle2 className="w-5 h-5 inline mr-1 text-emerald-500" /> Sem incidentes no período selecionado.
                </td></tr>
              )}
              {(logs?.rows || []).map((r, i) => {
                const kindStyle = r.kind === "5xx" || r.kind === "exception"
                  ? "bg-rose-100 text-rose-700"
                  : r.kind === "429" ? "bg-amber-100 text-amber-700"
                  : "bg-sky-100 text-sky-700";
                const kindLabel = r.kind === "5xx" ? "Erro 5xx" : r.kind === "exception" ? "Exceção" : r.kind === "429" ? "Bloqueio" : "Lento";
                const when = r.minute ? new Date(r.minute * 1000) : null;
                return (
                  <tr key={`${r.minute}:${r.kind}:${r.status}:${r.path}:${i}`} data-testid={`incident-row-${i}`}>
                    <td className="px-4 py-2 text-slate-500 text-xs tabular-nums whitespace-nowrap">
                      {when ? when.toLocaleString("pt-PT") : "—"}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${kindStyle}`}>{kindLabel}</span>
                    </td>
                    <td className="px-4 py-2 tabular-nums font-semibold text-slate-700">{r.status || "—"}</td>
                    <td className="px-4 py-2 font-mono text-[11px] text-slate-700 break-all">
                      <span className="text-slate-400">{r.method}</span> {r.path}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums font-bold text-slate-800">{r.count}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-600">{fmtMs(r.max_ms)}</td>
                    <td className="px-4 py-2 text-[11px] text-rose-600 max-w-[22rem] truncate" title={r.last_detail}>{r.last_detail || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 text-[11px] text-slate-400">
          Eventos agregados por minuto e rota (ids colapsados em <code>*</code>). Captura automática de erros 5xx, exceções não tratadas, bloqueios 429 e pedidos acima de 3s. Guardado em <code>_error_logs</code> com expiração de {logs?.ttl_days ?? 14} dias.
        </div>
      </div>

      {/* Alert rules & recent history */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="px-4 py-3 border-b border-slate-100">
            <h2 className="font-bold text-slate-800 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-600" /> Regras de alerta (editáveis)
            </h2>
          </div>
          <ul className="divide-y divide-slate-100" data-testid="alerts-rules-list">
            {alerts.rules.length === 0 && (
              <li className="px-4 py-6 text-center text-slate-400 text-sm">Sem regras.</li>
            )}
            {alerts.rules.map((r) => {
              const isEditing = editingRule === r.id;
              return (
                <li key={r.id} className="px-4 py-3" data-testid={`rule-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 flex items-center gap-2">
                        {r.label}
                        <span className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${r.severity === "critical" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                          {r.severity}
                        </span>
                        {r.customized && (
                          <span className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 bg-sky-100 text-sky-700" title="Valor alterado no admin">
                            editado
                          </span>
                        )}
                        {r.enabled === false && (
                          <span className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 bg-slate-200 text-slate-600">
                            desativado
                          </span>
                        )}
                      </div>
                      {!isEditing && (
                        <div className="text-[11px] text-slate-500 mt-1">
                          Dispara ao ≥ <b>{r.threshold}{r.unit}</b> durante <b>{r.duration_sec}s</b>
                          {" · "}email após <b>{r.email_after_sec}s</b> ativo
                        </div>
                      )}
                    </div>
                    {!isEditing && (
                      <button
                        onClick={() => startEditRule(r)}
                        className="text-xs font-semibold text-sky-700 hover:text-sky-900"
                        data-testid={`edit-rule-${r.id}`}
                      >
                        Editar
                      </button>
                    )}
                  </div>
                  {isEditing && (
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid={`edit-form-${r.id}`}>
                      <label className="flex flex-col gap-1">
                        <span className="text-slate-500">Threshold ({r.unit})</span>
                        <input
                          type="number" step="0.1"
                          value={ruleDraft.threshold ?? 0}
                          onChange={(e) => setRuleDraft((s) => ({ ...s, threshold: e.target.value === "" ? "" : Number(e.target.value) }))}
                          className="border border-slate-200 rounded px-2 py-1"
                          data-testid={`edit-${r.id}-threshold`}
                        />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span className="text-slate-500">Duração (s)</span>
                        <input
                          type="number" min="1"
                          value={ruleDraft.duration_sec ?? 0}
                          onChange={(e) => setRuleDraft((s) => ({ ...s, duration_sec: e.target.value === "" ? "" : Number(e.target.value) }))}
                          className="border border-slate-200 rounded px-2 py-1"
                          data-testid={`edit-${r.id}-duration`}
                        />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span className="text-slate-500">Email após (s)</span>
                        <input
                          type="number" min="0"
                          value={ruleDraft.email_after_sec ?? 0}
                          onChange={(e) => setRuleDraft((s) => ({ ...s, email_after_sec: e.target.value === "" ? "" : Number(e.target.value) }))}
                          className="border border-slate-200 rounded px-2 py-1"
                          data-testid={`edit-${r.id}-email-after`}
                        />
                      </label>
                      <label className="flex items-center gap-2 mt-5">
                        <input
                          type="checkbox"
                          checked={ruleDraft.enabled !== false}
                          onChange={(e) => setRuleDraft((s) => ({ ...s, enabled: e.target.checked }))}
                          data-testid={`edit-${r.id}-enabled`}
                        />
                        <span className="text-slate-600">Ativa</span>
                      </label>
                      <div className="col-span-full flex items-center gap-2 mt-1">
                        <button
                          onClick={() => saveRule(r.id)}
                          disabled={savingRule}
                          className="px-3 py-1 rounded bg-sky-600 text-white text-xs font-bold hover:bg-sky-700 disabled:opacity-60"
                          data-testid={`save-rule-${r.id}`}
                        >
                          Guardar
                        </button>
                        <button
                          onClick={cancelEditRule}
                          disabled={savingRule}
                          className="px-3 py-1 rounded bg-slate-100 text-slate-700 text-xs font-semibold hover:bg-slate-200"
                        >
                          Cancelar
                        </button>
                        {r.customized && (
                          <button
                            onClick={() => resetRule(r.id)}
                            disabled={savingRule}
                            className="px-3 py-1 rounded text-xs font-semibold text-rose-700 hover:bg-rose-50"
                            data-testid={`reset-rule-${r.id}`}
                          >
                            Repor default
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="px-4 py-3 border-t border-slate-100 text-[11px] text-slate-400">
            Overrides guardados em <code>_alert_rules_config</code>. Email enviado ao destinatário de <b>SMTP → Email para alertas</b>.
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="px-4 py-3 border-b border-slate-100">
            <h2 className="font-bold text-slate-800 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Alertas recentes (resolvidos)
            </h2>
          </div>
          <ul className="divide-y divide-slate-100 max-h-80 overflow-y-auto" data-testid="alerts-recent-list">
            {alerts.recent.length === 0 && (
              <li className="px-4 py-6 text-center text-slate-400 text-sm">Sem alertas resolvidos ainda.</li>
            )}
            {alerts.recent.map((a) => {
              const started = new Date((a.started_at || 0) * 1000);
              const ended = new Date((a.resolved_at || 0) * 1000);
              const durationSec = Math.max(0, Math.round((a.resolved_at || 0) - (a.started_at || 0)));
              return (
                <li key={`${a.rule_id}:${a.pod_id}:${a.started_at}`} className="px-4 py-2" data-testid={`recent-alert-${a.rule_id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-700 text-sm">{a.label}</span>
                    <span className="text-[11px] text-slate-500 tabular-nums">{fmtDuration(durationSec)}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Pico <b className="tabular-nums">{a.peak_value}{a.unit}</b>
                    {" · "}começou {started.toLocaleTimeString("pt-PT")}
                    {" · "}terminou {ended.toLocaleTimeString("pt-PT")}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Sessions modal — abre ao clicar no cartão "Sessões em curso" */}
      {sessionsOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4"
          role="dialog"
          data-testid="sessions-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setSessionsOpen(false); }}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-bold text-slate-800 flex items-center gap-2">
                <Zap className="w-4 h-4 text-sky-600" /> Sessões em curso
                <span className="text-xs font-normal text-slate-500">
                  ({sessionsData?.count ?? 0} total · auto-refresh 5s)
                </span>
              </h2>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 mr-2">
                  {["all", "competition", "eliminatoria", "vs_human"].map((k) => {
                    const label = k === "all" ? "Todos" : k === "vs_human" ? "VS Humano" : k === "eliminatoria" ? "Eliminatória" : "Competição";
                    const c = k === "all"
                      ? (sessionsData?.count ?? 0)
                      : (sessionsData?.by_kind?.[k] ?? 0);
                    return (
                      <button
                        key={k}
                        onClick={() => setSessionsFilter(k)}
                        className={`px-2.5 py-1 text-xs font-semibold rounded ${sessionsFilter === k ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                        data-testid={`sessions-filter-${k}`}
                      >
                        {label} <span className="opacity-70">({c})</span>
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setSessionsOpen(false)}
                  className="w-8 h-8 rounded hover:bg-slate-100 text-slate-500 grid place-items-center"
                  data-testid="sessions-modal-close"
                  aria-label="Fechar"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="overflow-auto flex-1">
              {sessionsLoading && !sessionsData ? (
                <div className="p-8 text-center text-slate-400 text-sm">A carregar…</div>
              ) : (sessionsData?.sessions || []).length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">Sem sessões em curso.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0 text-slate-600 text-[11px] uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-2">Tipo</th>
                      <th className="text-left px-4 py-2">Jogo</th>
                      <th className="text-left px-4 py-2">Jogador(es)</th>
                      <th className="text-left px-4 py-2">Detalhes</th>
                      <th className="text-left px-4 py-2">Iniciou</th>
                      <th className="text-right px-4 py-2">Duração</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-testid="sessions-table-body">
                    {(sessionsData?.sessions || [])
                      .filter((s) => sessionsFilter === "all" || s.kind === sessionsFilter)
                      .map((s) => {
                        const kindStyle = s.kind === "competition"
                          ? "bg-emerald-100 text-emerald-700"
                          : s.kind === "eliminatoria"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-violet-100 text-violet-700";
                        const started = s.started_at ? new Date(s.started_at) : null;
                        return (
                          <tr key={`${s.kind}:${s.id}`} data-testid={`session-row-${s.id}`}>
                            <td className="px-4 py-2">
                              <span className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${kindStyle}`}>
                                {s.kind}
                              </span>
                            </td>
                            <td className="px-4 py-2 font-semibold text-slate-800">{s.game_name || "—"}</td>
                            <td className="px-4 py-2 text-slate-700">{s.player_label || "—"}</td>
                            <td className="px-4 py-2 text-slate-500 text-xs">{s.player_extra || "—"}</td>
                            <td className="px-4 py-2 text-slate-500 text-xs tabular-nums">
                              {started ? started.toLocaleString("pt-PT") : "—"}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-slate-600">
                              {s.age_sec != null ? fmtDuration(s.age_sec) : "—"}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[11px] text-slate-400 flex-1 min-w-[240px]">
                Uma sessão fica <code>in_progress</code> enquanto o jogador não terminar. Se aparecerem entradas muito antigas, o jogador provavelmente fechou o browser a meio.
              </div>
              <div className="flex items-center gap-2" data-testid="cleanup-controls">
                <label className="text-xs font-semibold text-slate-600">
                  Limpar &gt;
                  <input
                    type="number"
                    min="1"
                    value={cleanupHours}
                    onChange={(e) => setCleanupHours(e.target.value)}
                    className="mx-1 w-16 h-8 border border-slate-200 rounded px-2 text-center tabular-nums"
                    data-testid="cleanup-hours-input"
                  />
                  h
                </label>
                <button
                  onClick={cleanupZombies}
                  disabled={cleaning}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-rose-600 text-white text-xs font-bold hover:bg-rose-700 disabled:opacity-60"
                  data-testid="cleanup-zombies-btn"
                >
                  {cleaning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  Limpar sessões zombies
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
